import { useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { MarketProvider, useMarketData } from './context/MarketContext';
import { useAuth } from './context/AuthContext';
import type { PageKey } from './types';

// Components
import { AppHeader } from './components/AppHeader';
import { AppLayout } from './components/AppLayout';
import ToastNotifications from './components/ToastNotifications';
import DailySummaryModal from './components/DailySummaryModal';
import FloatingOrderFAB from './components/FloatingOrderFAB';
import FloatingQuoteSuggestion from './components/FloatingQuoteSuggestion';
import { ErrorBoundary } from './components/ui';

// Pages
import DashboardPage from './pages/DashboardPage';
import PositionsPage from './pages/PositionsPage';
import PostTradePage from './pages/PostTradePage';
import MarketStatsPage from './pages/MarketStatsPage';
import OrdersPage from './pages/OrdersPage';
import AnalysisPage from './pages/AnalysisPage';
import QuotesPage from './pages/QuotesPage';
import EtfPage from './pages/EtfPage';
import RiskPage from './pages/RiskPage';
import SettingsPage from './pages/SettingsPage';
import ControlsPage from './pages/ControlsPage';
import LoginPage from './pages/LoginPage';

/** Map URL paths to PageKey values (used by Navigation to highlight active item) */
const PATH_TO_PAGE: Record<string, PageKey> = {
  '/':            'dashboard',
  '/dashboard':   'dashboard',
  '/positions':   'positions',
  '/posttrade':   'posttrade',
  '/marketstats': 'marketstats',
  '/orders':      'orders',
  '/analysis':    'analysis',
  '/quotes':      'quotes',
  '/etf':         'etf',
  '/risk':        'risk',
  '/settings':    'settings',
  '/controls':    'controls',
};

const PAGE_TO_PATH: Record<PageKey, string> = {
  dashboard:   '/dashboard',
  positions:   '/positions',
  posttrade:   '/posttrade',
  marketstats: '/marketstats',
  orders:      '/orders',
  analysis:    '/analysis',
  quotes:      '/quotes',
  etf:         '/etf',
  risk:        '/risk',
  settings:    '/settings',
  controls:    '/controls',
};

export { PATH_TO_PAGE, PAGE_TO_PATH };

function AppContent() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#0a0d10', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d7dde4' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) return <LoginPage />;

  return <AuthenticatedApp />;
}

const WATCHLIST_KEY = 'shabba:watchlist';

function AuthenticatedApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const activePage = PATH_TO_PAGE[location.pathname] ?? 'dashboard';
  const setActivePage = (page: PageKey) => navigate(PAGE_TO_PATH[page]);

  const [selectedBookPrice, setSelectedBookPrice] = useState<number | undefined>(undefined);
  const [infoTab, setInfoTab] = useState<'inventory' | 'settlement' | 'trades'>('inventory');

  const { INSTRUMENTS } = useMarketData();

  // Dynamic watchlist — persisted to localStorage
  const [instruments, setInstruments] = useState<{ label: string; secId: string }[]>(() => {
    try {
      const stored = localStorage.getItem(WATCHLIST_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {}
    return INSTRUMENTS;
  });

  const addInstrument = (label: string, secId: string) => {
    setInstruments(prev => {
      const next = [...prev, { label: label.toUpperCase().trim(), secId: secId.toUpperCase().trim() }];
      localStorage.setItem(WATCHLIST_KEY, JSON.stringify(next));
      return next;
    });
  };

  const removeInstrument = (secId: string) => {
    setInstruments(prev => {
      const next = prev.filter(i => i.secId !== secId);
      localStorage.setItem(WATCHLIST_KEY, JSON.stringify(next));
      return next;
    });
  };

  const pageRoutes = (
    <Routes>
      <Route path="/dashboard"   element={<ErrorBoundary name="Dashboard"><DashboardPage instruments={instruments} addInstrument={addInstrument} removeInstrument={removeInstrument} /></ErrorBoundary>} />
      <Route path="/positions"   element={<ErrorBoundary name="Positions"><PositionsPage /></ErrorBoundary>} />
      <Route path="/posttrade"   element={<ErrorBoundary name="Post-Trade"><PostTradePage /></ErrorBoundary>} />
      <Route path="/marketstats" element={<ErrorBoundary name="Market Stats"><MarketStatsPage /></ErrorBoundary>} />
      <Route path="/orders"      element={<ErrorBoundary name="Orders"><OrdersPage /></ErrorBoundary>} />
      <Route path="/analysis"    element={<ErrorBoundary name="Analysis"><AnalysisPage /></ErrorBoundary>} />
      <Route path="/quotes"      element={<ErrorBoundary name="Quotes"><QuotesPage /></ErrorBoundary>} />
      <Route path="/etf"         element={<ErrorBoundary name="ETF"><EtfPage /></ErrorBoundary>} />
      <Route path="/risk"        element={<ErrorBoundary name="Risk"><RiskPage /></ErrorBoundary>} />
      <Route path="/settings"    element={<ErrorBoundary name="Settings"><SettingsPage /></ErrorBoundary>} />
      <Route path="/controls"    element={<ErrorBoundary name="Controls"><ControlsPage /></ErrorBoundary>} />
      <Route path="*"            element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );

  return (
    <div className="app-container" style={{
      backgroundColor: '#0a0d10',
      color: '#d7dde4',
      minHeight: '100vh',
      width: '100%',
      minWidth: '100%',
      fontFamily: 'Sora, -apple-system, BlinkMacSystemFont, sans-serif',
      fontSize: '13px',
      paddingTop: 52,
      // Bottom chrome = 46px nav rail + 29px status bar (height:28 plus a 1px
      // border, no box-sizing). Both are position:fixed, so the shell must
      // reserve the space or the last row of any page is hidden behind them.
      paddingBottom: 75,
      boxSizing: 'border-box',
    }}>
      <AppHeader
        onNavigate={setActivePage}
        instruments={instruments}
      />
      <AppLayout
        activePage={activePage}
        setActivePage={setActivePage}
        instruments={instruments}
        addInstrument={addInstrument}
        removeInstrument={removeInstrument}
        selectedBookPrice={selectedBookPrice}
        setSelectedBookPrice={setSelectedBookPrice}
        infoTab={infoTab}
        setInfoTab={setInfoTab}
        renderPage={() => pageRoutes}
      />
      <FloatingOrderFAB prefillPrice={selectedBookPrice} />
    </div>
  );
}

function App() {
  return (
    <MarketProvider>
      <AppContent />
      <ToastNotifications />
      <FloatingQuoteSuggestion />
      <DailySummaryModal />
    </MarketProvider>
  );
}

export default App;
